import { useTranslation } from 'react-i18next'
import { Badge, Card } from '@/components/ui'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { formatBaht } from '@/lib/money'
import type { Subscription } from '@/hooks/useSubscription'
import { useSubscriptionItemLabel } from './itemLabel'

export interface SubscriptionTierCardProps {
  subscription: Subscription
}

/**
 * EP-144 T144-008: the current plan plus the next-invoice estimate.
 *
 * Every figure is rendered exactly as the server sent it. There is NO pricing
 * math here: `quantity` and `unit_price` are printed side by side because the
 * engine already computed `amount`, and `total` is the engine's own total. The
 * only numeric handling is Number() on the decimal strings the API returns,
 * which is a formatting coercion, not arithmetic.
 */
export function SubscriptionTierCard({ subscription }: SubscriptionTierCardProps) {
  const { t } = useTranslation('settings')
  const formatDate = useDateFormatter()
  const preview = subscription.next_invoice_preview
  // Shared with the invoice detail page so the estimate and the issued invoice
  // can never localize the same engine line two different ways.
  const itemLabel = useSubscriptionItemLabel()

  const rows: Array<{ key: string; label: string; value: string }> = [
    { key: 'tier', label: t('subscription.tier_label'), value: t(`subscription.tiers.${subscription.tier}`) },
    {
      key: 'billing_cycle',
      label: t('subscription.billing_cycle_label'),
      value: t(`subscription.cycles.${subscription.billing_cycle}`),
    },
    { key: 'room_count', label: t('subscription.room_count_label'), value: String(subscription.room_count) },
    { key: 'started_at', label: t('subscription.started_at_label'), value: formatDate(subscription.started_at) },
  ]

  return (
    <Card
      title={t('subscription.plan.title')}
      actions={
        <Badge variant={subscription.status === 'active' ? 'success' : 'ghost'}>
          {t(`subscription.statuses.${subscription.status}`)}
        </Badge>
      }
    >
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
        {rows.map((row) => (
          <div key={row.key} className="contents">
            <dt className="min-w-0 text-sm text-base-content/60">{row.label}</dt>
            <dd className="min-w-0 text-sm font-medium text-right sm:text-left">{row.value}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-6 border-t border-base-300 pt-4">
        <h3 className="font-semibold text-sm">{t('subscription.next_invoice.title')}</h3>
        <p className="text-sm text-base-content/60">
          {t('subscription.next_invoice.period', {
            from: formatDate(preview.period_from),
            to: formatDate(preview.period_to),
          })}
        </p>

        <ul className="mt-3 space-y-2">
          {preview.items.map((item, index) => (
            <li key={`${item.description_key}-${index}`} className="flex flex-wrap items-baseline gap-x-3 text-sm">
              <span className="min-w-0 flex-1">{itemLabel(item)}</span>
              <span className="min-w-0 text-base-content/60 tabular-nums">
                {item.quantity} × {formatBaht(item.unit_price)}
              </span>
              <span className="min-w-0 font-medium tabular-nums">{formatBaht(item.amount)}</span>
            </li>
          ))}
        </ul>

        <div className="mt-3 flex items-baseline justify-between border-t border-base-300 pt-3 text-sm font-semibold">
          <span className="min-w-0">{t('subscription.next_invoice.total')}</span>
          <span className="min-w-0 tabular-nums">{formatBaht(preview.total)}</span>
        </div>

        <p className="mt-2 text-xs text-base-content/60">{t('subscription.next_invoice.note')}</p>
      </div>
    </Card>
  )
}
